import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Role } from '@tp-vision/roles-permissions';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { InfoAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useServiceAdminUserUpdateMutation } from './__generated__/EditUserModal.graphql';
import {
  getEditableRoleOptions,
  getUserManagementErrorMap,
  roleOptionsSchema,
  RolesField,
  toEditableRoleOptions,
} from './userManagement';

const schema = z.object({
  email: z.string().min(1).email(),
  roles: roleOptionsSchema,
});

type FormValues = z.infer<typeof schema>;

export interface EditableUser {
  id: string;
  email: string;
  givenName?: string | null;
  familyName?: string | null;
  roles: Role[];
}

interface Props {
  user: EditableUser | null;
  onClose: () => void;
  onUpdated: () => void;
}

export function EditUserModal({ user, onClose, onUpdated }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [updateUser] = useServiceAdminUserUpdateMutation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (user) {
      reset({ email: user.email, roles: toEditableRoleOptions(user.roles) });
    }
  }, [user, reset]);

  const isEmailChanged =
    !!user && watch('email')?.trim().toLowerCase() !== user.email.toLowerCase();

  const onSubmit = async ({ email, roles }: FormValues) => {
    if (!user) {
      return;
    }

    try {
      await updateUser({
        variables: {
          input: { userId: user.id, email: email.trim(), roles: roles.map(({ value }) => value) },
        },
      });

      toast({
        status: 'success',
        title: (
          <Trans
            i18nKey={
              isEmailChanged ? 'adminSearchUserUpdatedEmailChanged' : 'adminSearchUserUpdated'
            }
            values={{
              name: [user.givenName, user.familyName].join(' ').trim(),
              email: email.trim(),
            }}
            components={{ strong: <strong /> }}
          />
        ),
      });

      onUpdated();
      onClose();
    } catch (err) {
      toast({
        status: 'error',
        title: t('adminSearchUserUpdateError'),
        description: fromError(err, 'ServiceAdminUserUpdate', getUserManagementErrorMap()),
      });
    }
  };

  return (
    <Modal isOpen={Boolean(user)} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>{t('editUser')}</ModalHeader>
        <ModalCloseButton onClick={onClose} />
        <ModalBody>
          <Stack spacing="5">
            <FormControl isReadOnly>
              <FormLabel>{t('firstName')}</FormLabel>
              <Input value={user?.givenName ?? ''} isReadOnly bgColor="gray.50" />
            </FormControl>
            <FormControl isReadOnly>
              <FormLabel>{t('lastName')}</FormLabel>
              <Input value={user?.familyName ?? ''} isReadOnly bgColor="gray.50" />
            </FormControl>
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel>{t('email')}</FormLabel>
              <Input type="email" {...register('email')} />
              <FormErrorMessage>{t('invalidEmail')}</FormErrorMessage>
            </FormControl>
            <RolesField
              control={control}
              name="roles"
              error={errors.roles as never}
              options={getEditableRoleOptions()}
            />
            <InfoAlert>{t('adminSearchEmailChangeInviteNotice')}</InfoAlert>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" marginRight="3" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} isDisabled={!isDirty}>
            {t('save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

EditUserModal.graphql = {
  mutations: {
    ServiceAdminUserUpdate: gql`
      mutation ServiceAdminUserUpdate($input: ServiceAdminUserUpdateInput!) {
        serviceAdminUserUpdate(input: $input) {
          id
          email
          roles
        }
      }
    `,
  },
};
